import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { ThreeDots } from 'react-loader-spinner';
import DefaultLayout from '../reusableComponents/defaultLayout';
import AccountHeader, { subContractorTabs } from '../components/account/AccountHeader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import { API_BASE_URL } from '../config/api';

export default function Submitproposal() {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { pid } = useParams();
    const userRole = useSelector((state) => state?.userProfileSlice?.userData?.data?.role);
    const isSubContractor = userRole === 'sub_contractor';

    const planDocument = useRef(null);
    const additionalDocument = useRef(null);

    const [selectedPlanDoc, setSelectedPlanDoc] = useState([]);
    const [selectedAdditionalDoc, setSelectedAdditionalDoc] = useState([]);

    const handlePlanDocChange = (event, setFieldValue) => {
        const selectedDoc = Array.from(event.currentTarget.files);
        setSelectedPlanDoc(prevDocs => [...prevDocs, ...selectedDoc]);
        setFieldValue("plan_doc", selectedDoc);
    };

    const handleAdditionalDocChange = (event, setFieldValue) => {
        const selectedDoc = Array.from(event.currentTarget.files);
        setSelectedAdditionalDoc(prevDocs => [...prevDocs, ...selectedDoc]);
        setFieldValue("additional_doc", selectedDoc);
    };

    const handleDeleteDocument = (event, index, documentType, setFieldValue) => {
        event.preventDefault();
        if (documentType === "plan_doc") {
            const updatedDocs = [...selectedPlanDoc];
            updatedDocs.splice(index, 1);
            setSelectedPlanDoc(updatedDocs);
            setFieldValue("plan_doc", updatedDocs);
        } else if (documentType === "additional_doc") {
            const updatedDocs = [...selectedAdditionalDoc];
            updatedDocs.splice(index, 1);
            setSelectedAdditionalDoc(updatedDocs);
            setFieldValue("additional_doc", updatedDocs);
        }
    };

    const initialValues = {
        plan_doc: [],
        additional_doc: [],
        project_description: '',
    };

    const validationSchema = Yup.object().shape({
        project_description: Yup.string().required("Comments are required"),
    });

    const submitProposal = async (values) => {
        setLoading(true);

        const url = API_BASE_URL;
        const token = localStorage.getItem('authToken');

        if (token) {
            try {
                const formData = new FormData();

                selectedPlanDoc.forEach((plan) => {
                    formData.append("plan_doc[]", plan);
                });

                selectedAdditionalDoc.forEach((additional) => {
                    formData.append("additional_doc[]", additional);
                });

                formData.append("project_id", pid);
                if (values.project_description?.length) {
                    formData.append('description', values.project_description);
                }

                const requestOptions = {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                };

                const response = await fetch(`${url}/create_bid`, requestOptions);
                const { message, success } = await response.json();

                if (success) {
                    toast.success('Proposal submitted successfully!', { autoClose: 3000 });
                    navigate('/account/2');
                } else {
                    toast.error(message, { autoClose: 3000 });
                }
            } catch (error) {
                console.error('An error occurred:', error);
                toast.error('An error occurred: ' + error, { autoClose: 3000 });
            } finally {
                setLoading(false);
            }
        }
    };

    const triggerFileInput = (ref) => {
        ref.current?.click();
    };

    return (
        <DefaultLayout>
            <AccountHeader tabs={isSubContractor ? subContractorTabs : undefined} />

            <div className="profile_page">
                <div className="container">
                    <button className="back_link" onClick={() => navigate(-1)}>
                        <ArrowBackIcon fontSize="small" /> Back to Project
                    </button>

                    <div className="page_title_header">
                        <h1>Submit Proposal</h1>
                        <p>Provide your proposal documents and comments for the project owner</p>
                    </div>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={submitProposal}
                    >
                        {({ values, setFieldValue, errors, touched }) => (
                            <Form>
                                {/* Proposal Documents Section */}
                                <div className="profile_section">
                                    <div className="section_header">
                                        <h3>Proposal Documents</h3>
                                        <p>Upload your proposal documents in PDF format</p>
                                    </div>
                                    <div className="section_content">
                                        <div className="documents_upload_grid">
                                            {/* Main Proposal Documents */}
                                            <div className="document_upload_card">
                                                <div className="upload_card_header">
                                                    <InsertDriveFileIcon />
                                                    <div>
                                                        <h4>Proposal Documents</h4>
                                                        <span>PDF format only</span>
                                                    </div>
                                                </div>
                                                
                                                <div 
                                                    className="file_dropzone compact"
                                                    onClick={() => triggerFileInput(planDocument)}
                                                >
                                                    <input
                                                        type="file"
                                                        name="plan_doc"
                                                        multiple
                                                        accept=".pdf"
                                                        ref={planDocument}
                                                        onChange={(event) => handlePlanDocChange(event, setFieldValue)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <CloudUploadIcon className="dropzone_icon" />
                                                    <p>Click to upload PDF files</p>
                                                </div>

                                                {selectedPlanDoc.length > 0 && (
                                                    <div className="uploaded_files_list">
                                                        {selectedPlanDoc.map((plan, index) => (
                                                            <div key={index} className="uploaded_file_item">
                                                                <InsertDriveFileIcon fontSize="small" />
                                                                <span>{plan.name.length > 30 ? `${plan.name.slice(0, 30)}...` : plan.name}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={(event) => handleDeleteDocument(event, index, "plan_doc", setFieldValue)}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Additional Documents */}
                                            <div className="document_upload_card">
                                                <div className="upload_card_header">
                                                    <InsertDriveFileIcon />
                                                    <div>
                                                        <h4>Additional Documents</h4>
                                                        <span>Supporting materials (optional)</span>
                                                    </div>
                                                </div>
                                                
                                                <div 
                                                    className="file_dropzone compact"
                                                    onClick={() => triggerFileInput(additionalDocument)}
                                                >
                                                    <input
                                                        type="file"
                                                        name="additional_doc"
                                                        multiple
                                                        accept=".pdf"
                                                        ref={additionalDocument}
                                                        onChange={(event) => handleAdditionalDocChange(event, setFieldValue)}
                                                        style={{ display: 'none' }}
                                                    />
                                                    <CloudUploadIcon className="dropzone_icon" />
                                                    <p>Click to upload PDF files</p>
                                                </div>

                                                {selectedAdditionalDoc.length > 0 && (
                                                    <div className="uploaded_files_list">
                                                        {selectedAdditionalDoc.map((doc, index) => (
                                                            <div key={index} className="uploaded_file_item">
                                                                <InsertDriveFileIcon fontSize="small" />
                                                                <span>{doc.name.length > 30 ? `${doc.name.slice(0, 30)}...` : doc.name}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={(event) => handleDeleteDocument(event, index, "additional_doc", setFieldValue)}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Comments Section */}
                                <div className="profile_section">
                                    <div className="section_header">
                                        <h3>Comments for Project Owner</h3>
                                        <p>Provide details about your proposal and why you're the right fit</p>
                                    </div>
                                    <div className="section_content">
                                        <div className="form_group">
                                            <label htmlFor="project_description">
                                                <DescriptionIcon fontSize="small" />
                                                Your Message
                                            </label>
                                            <Field
                                                as="textarea"
                                                id="project_description"
                                                name="project_description"
                                                className={`form-control textarea_large ${errors.project_description && touched.project_description ? 'is-invalid' : ''}`}
                                                placeholder="Describe your experience, approach, timeline, and any relevant details that would help the project owner make their decision..."
                                                rows={6}
                                            />
                                            <ErrorMessage name="project_description" component="div" className="field_error" />
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Actions */}
                                <div className="profile_actions">
                                    <button type="button" className="cancel_btn" onClick={() => navigate(-1)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="save_btn" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <ThreeDots height="20" width="40" color="#fff" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <SendIcon fontSize="small" />
                                                Submit Proposal
                                            </>
                                        )}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </DefaultLayout>
    );
}
